import { ComponentDesignSealTimestamps, Document, Job, JobType, Pdf } from '@paragon/contract-library';

import { SealingService } from '../connections';

export function fetch(jobId: number): Promise<Job> {
  return SealingService.get(`jobs/${jobId}`);
}

export function postJob(job: Partial<Job>): Promise<Job> {
  return SealingService.post('jobs', { data: job });
}

export function downloadMerged(jobId: number) {
  return SealingService.fetchFile(`jobs/${jobId}/mergedPdf`, { mightEmail: true });
}

export function downloadSeparated(jobId: number) {
  return SealingService.fetchFile(`jobs/${jobId}/separatedPdfs`, { mightEmail: true });
}

export function downloadMiscellaneous(jobId: number) {
  return SealingService.fetchFile(`jobs/${jobId}/miscellaneousFiles`);
}

export function downloadZip(jobId: number) {
  return SealingService.fetchFile(`jobs/${jobId}/zippedFiles`);
}

export function open(id: number) {
  return SealingService.patch(`jobs/${id}/open`);
}

export function close(id: number) {
  return SealingService.post(`jobs/${id}/close`);
}

export function reset(jobId: number): Promise<void> {
  return SealingService.post(`jobs/${jobId}/resetByUser`);
}

export function resetSeals(jobId: number): Promise<void> {
  return SealingService.post(`jobs/${jobId}/resetSeals`);
}

export function rejectJob(jobId: number) {
  return SealingService.post(`jobs/${jobId}/reject`);
}

export function returnJob(jobId: number) {
  return SealingService.post(`jobs/${jobId}/return`);
}

export function lock(jobId: number) {
  return SealingService.post(`jobs/${jobId}/lock`);
}

export function unlock(jobId: number) {
  return SealingService.post(`jobs/${jobId}/unlock`);
}

export function getDocumentsForJob(jobId: number): Promise<readonly Document[]> {
  return SealingService.get(`jobs/${jobId}/Documents`);
}

export function getDocument(pdfId: number): Promise<Document> {
  return SealingService.get(`jobs/Documents/${pdfId}`);
}

export function addMetaPdf(
  jobId: number,
  pdfsToUpload: readonly FileToUpload[],
): Promise<{ addedPdfs: Pdf[]; corruptPdfs: readonly string[] }> {
  return SealingService.patch(`jobs/${jobId}/metaPdf`, { data: pdfsToUpload });
}

interface FileToUpload {
  readonly name: string;
  readonly index?: number;
  readonly url: string | undefined;
}

export function addMiscellaneousFiles(jobId: number, filesToUpload: readonly FileToUpload[]): Promise<void> {
  return SealingService.patch(`jobs/${jobId}/miscellaneousFiles`, { data: filesToUpload });
}

// TODO type for indexMap
export function reindexPdfs(jobId: number, indexMap: any) {
  const indexMapForUpload = Object.keys(indexMap).map((key) => ({ id: parseInt(key, 10), index: indexMap[key] }));

  return SealingService.patch(`jobs/${jobId}/reindexPdfs`, { data: indexMapForUpload });
}

export function patchReindexPdfsNaturalSort(jobId: number): Promise<{ [id: number]: number }> {
  return SealingService.patch(`jobs/${jobId}/reindexPdfsNaturalSort`);
}

interface ComponentDesignGuidsAndMetadata {
  jobType: JobType;
  isParagonJob: boolean;
  sealed: boolean;
  locked: boolean;
  ownerOrganization: string;
  sharedWithOrganizations: string[];
  componentDesignGuids: string[];
}

export function getComponentDesignGuidsAndMetadata(jobId: number): Promise<ComponentDesignGuidsAndMetadata> {
  return SealingService.get(`jobs/${jobId}/componentDesignGuidsAndMetadata`);
}

export function getComponentDesignSealTimestamps(projectGuid: string): Promise<ComponentDesignSealTimestamps[]> {
  return SealingService.get(`jobs/${projectGuid}/componentDesignSealTimestamps`);
}

export function validatePdfs(pdfsToValidate: readonly { name: string; base64Pdf: string }[]) {
  return SealingService.post<{ corruptPdfFileNames: readonly string[] }>(`jobs/validatePdfs`, {
    data: pdfsToValidate,
  });
}
